import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, MessageSquare, ArrowRight, AlertTriangle, MapPin, ChevronLeft, HelpCircle } from "lucide-react";
import * as Icons from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { SERVICE_PAGES, findServicePage } from "../data/servicePages";
import { SERVICE_FAQS } from "../data/servicePageFaqs";
import { GarageNote } from "../components/GarageNote";
import { useSeo } from "../hooks/useSeo";

const PHONE_SMS = "sms:+14047497063";
const AREAS = ["Las Vegas", "Henderson", "Enterprise", "Spring Valley", "Summerlin", "Southern Highlands", "Mountains Edge", "Southwest Las Vegas"];

const CtaRow = ({ page }) => (
  <div className="flex flex-col sm:flex-row gap-3" data-testid="service-cta-row">
    <a data-testid="service-call-btn" href={PHONE_TEL} className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-4 font-semibold transition-colors">
      <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
    </a>
    <a data-testid="service-text-btn" href={PHONE_SMS} className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-600 hover:border-white text-white rounded-sm px-6 py-4 font-semibold transition-colors">
      <MessageSquare className="h-5 w-5" /> Text Us
    </a>
    <Link data-testid="service-estimate-btn" to="/contact" className="flex-1 inline-flex items-center justify-center gap-2 border border-blue-500/50 hover:border-blue-400 text-blue-400 rounded-sm px-6 py-4 font-semibold transition-colors">
      Request an Estimate <ArrowRight className="h-5 w-5" />
    </Link>
  </div>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const page = findServicePage(slug);
  const faqs = (page && SERVICE_FAQS[page.slug]) || [];

  const jsonLd = useMemo(() => {
    if (!page) return null;
    const service = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.name,
      serviceType: page.name,
      provider: { "@id": "https://masonsmobile.com/#business" },
      areaServed: AREAS.map((a) => ({ "@type": "Place", name: a })),
      description: page.metaDesc,
      url: `https://masonsmobile.com/services/${page.slug}`,
    };
    if (!faqs.length) return service;
    return [service, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }];
  }, [page, faqs]);

  useSeo({
    title: page?.seoTitle,
    description: page?.metaDesc,
    canonical: page ? `https://masonsmobile.com/services/${page.slug}` : undefined,
    jsonLd,
  });

  if (!page) return <Navigate to="/services" replace />;

  const Icon = Icons[page.icon] || Icons.Wrench;
  const related = SERVICE_PAGES.filter((p) => p.slug !== page.slug).slice(0, 20);

  return (
    <main data-testid="service-detail-page" className="pt-20 md:pt-24">
      <section className="py-16 sm:py-24 border-b border-zinc-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/services" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm mb-8 transition-colors" data-testid="back-to-services">
            <ChevronLeft className="h-4 w-4" /> All Services
          </Link>
          <div className="bg-blue-600/10 border border-blue-500/20 rounded-sm w-12 h-12 flex items-center justify-center mb-6">
            <Icon className="h-6 w-6 text-blue-500" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-4">{page.h1}</h1>
          <p className="text-blue-400 font-medium mb-6">{page.local}</p>
          {page.desc.map((d) => (
            <p key={d.slice(0, 24)} className="text-zinc-400 leading-relaxed mb-4">{d}</p>
          ))}
          <div className="mt-8"><CtaRow page={page} /></div>
        </div>
      </section>

      <section className="py-16 border-b border-zinc-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-500" /> Common Warning Signs
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="warning-signs-list">
            {page.signs.map((s) => (
              <li key={s} className="flex items-start gap-2.5 bg-[#121212] border border-zinc-800 rounded-sm px-4 py-3 text-zinc-300 text-sm">
                <span className="text-blue-500 mt-0.5">•</span> {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 border-b border-zinc-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-10">
          <GarageNote />
          <div>
            <h2 className="font-heading text-2xl font-bold text-white mb-5 flex items-center gap-3">
              <MapPin className="h-5 w-5 text-blue-500" /> Areas We Serve
            </h2>
            <p className="text-zinc-400 mb-5">
              {page.name} is available throughout the Las Vegas Valley — we come to your
              home, workplace, or wherever the vehicle is parked.
            </p>
            <div className="flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span key={a} className="bg-[#121212] border border-zinc-800 rounded-full px-4 py-1.5 text-zinc-300 text-sm">{a}</span>
              ))}
            </div>
          </div>
          <CtaRow page={page} />
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="py-16 border-b border-zinc-800/60">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <HelpCircle className="h-5 w-5 text-blue-500" /> Frequently Asked Questions
            </h2>
            <div className="space-y-3" data-testid="service-faq-list">
              {faqs.map((f) => (
                <details key={f.q} className="group bg-[#121212] border border-zinc-800 rounded-sm open:border-blue-500/40 transition-colors">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-start justify-between gap-4 text-white font-medium text-sm sm:text-base">
                    {f.q}
                    <span className="text-blue-500 mt-0.5 shrink-0 transition-transform group-open:rotate-45 text-lg leading-none">+</span>
                  </summary>
                  <p className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-xl font-bold text-white mb-5">More Mobile Services</h2>
          <div className="flex flex-wrap gap-2">
            {related.map((p) => (
              <Link key={p.slug} to={`/services/${p.slug}`} className="bg-[#121212] border border-zinc-800 hover:border-blue-500/50 rounded-sm px-4 py-2 text-zinc-300 hover:text-white text-sm transition-colors">
                {p.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
